/**
 * RegenerateVisualsWizard — «Перегенерировать визуалы» в карточке макета.
 *
 * Прежняя кнопка спрашивала одно «точно?» и уходила делать всё: все товары
 * макета, кабинеты из прошлого прогона, воронки по правилам резолвера. Для
 * поправки одной картинки это перерисовывало десятки визуалов и заводило новые
 * ревизии черновиков там, где ничего не менялось.
 *
 * Мастер задаёт три вопроса — что, куда и чем:
 *   1) товары по матрице категорий (отсутствующие будут созданы);
 *   2) кабинеты (предвыбор — те, что уже участвовали в прогонах макета);
 *   3) контент-воронки на каждую пару «категория × кабинет».
 * Четвёртый шаг — выполнение: прогон идёт минутами, и человек должен видеть
 * стадии, а не тост «запущено».
 *
 * Единица выбора на первом шаге — категория, хотя показываются товары: визуалы
 * многотоварной категории движок рендерит одной задачей на все её артикулы.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Loader2,
  Minus,
  RefreshCw,
  XCircle,
} from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/ui/Modal'
import { Stepper, type Step } from '../../../components/ui/Stepper'
import { jobsAPI } from '../../jobs/api'
import ContentFunnelSelectionStep from '../../../components/ContentFunnelSelectionStep'
import {
  initialFunnelChoices,
  serializeFunnelSelections,
} from '../../../components/contentFunnelSelection.logic'
import type { FunnelPlanRow } from '../../../components/contentFunnelSelection.types'
import {
  pipelineAPI,
  type RegenerationFunnelPlan,
  type RegenerationPlan,
} from '../../pipeline/api'
import {
  cabinetKey,
  executionHeadline,
  executionStages,
  initialCabinets,
  isExecutionFailed,
  isExecutionFinished,
  productRows,
  selectedTargets,
  selectionSummary,
  toggleCategory,
} from './regenerateVisualsWizard.logic'

type WizardStep = 1 | 2 | 3 | 4
type LoadState = 'idle' | 'loading' | 'ready' | 'error'

interface RegenerateVisualsWizardProps {
  isOpen: boolean
  designCode: number | null
  onClose: () => void
  /** Прогон запущен — карточку макета стоит перечитать. */
  onLaunched: () => void
}

const errorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.error?.message || error?.message || fallback

export function RegenerateVisualsWizard({
  isOpen,
  designCode,
  onClose,
  onLaunched,
}: RegenerateVisualsWizardProps) {
  const [step, setStep] = useState<WizardStep>(1)
  const [plan, setPlan] = useState<RegenerationPlan | null>(null)
  const [planState, setPlanState] = useState<LoadState>('idle')
  const [planError, setPlanError] = useState('')
  const [categories, setCategories] = useState<number[]>([])
  const [cabinets, setCabinets] = useState<string[]>([])
  const [funnelPlan, setFunnelPlan] = useState<RegenerationFunnelPlan | null>(null)
  const [funnelState, setFunnelState] = useState<LoadState>('idle')
  const [funnelError, setFunnelError] = useState('')
  const [choices, setChoices] = useState<Record<string, string>>({})
  const [launching, setLaunching] = useState(false)
  const [launchError, setLaunchError] = useState('')
  const [jobId, setJobId] = useState('')
  const [job, setJob] = useState<any>(null)
  const [pollError, setPollError] = useState('')

  const code = designCode == null ? '' : String(designCode)

  const loadPlan = useCallback(async (target: string) => {
    setPlanState('loading')
    setPlanError('')
    try {
      const data = await pipelineAPI.getRegenerationPlan(target)
      setPlan(data)
      // По умолчанию — всё, что и делала кнопка без мастера: все товары макета
      // и кабинеты его прошлых прогонов.
      setCategories(data.categories.map((category) => category.category_id))
      setCabinets(initialCabinets(data.cabinets))
      setPlanState('ready')
    } catch (error: any) {
      setPlan(null)
      setPlanState('error')
      setPlanError(errorMessage(error, 'Не удалось загрузить товары и кабинеты макета'))
    }
  }, [])

  useEffect(() => {
    if (!isOpen || !code) return
    setStep(1)
    setFunnelPlan(null)
    setFunnelState('idle')
    setFunnelError('')
    setChoices({})
    setLaunchError('')
    setJobId('')
    setJob(null)
    setPollError('')
    void loadPlan(code)
  }, [isOpen, code, loadPlan])

  // Пока идёт прогон, состояние читаем из фоновой задачи: стадии считает
  // бэкенд, здесь только опрос.
  useEffect(() => {
    if (!isOpen || !jobId) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    const poll = async () => {
      try {
        const response = await jobsAPI.getJob(jobId)
        const raw = response.data?.data ?? response.data ?? null
        if (cancelled) return
        setJob(raw)
        setPollError('')
        if (!isExecutionFinished(raw)) timer = setTimeout(poll, 3000)
      } catch (error: any) {
        if (cancelled) return
        setPollError(errorMessage(error, 'Не удалось обновить состояние'))
        timer = setTimeout(poll, 5000)
      }
    }
    void poll()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [isOpen, jobId])

  const rows = useMemo(() => productRows(plan?.categories || []), [plan])
  const summary = useMemo(() => selectionSummary(plan, categories), [plan, categories])
  const targets = useMemo(
    () => selectedTargets(plan?.cabinets || [], cabinets),
    [plan, cabinets],
  )
  const funnelRows = (funnelPlan?.rows || []) as FunnelPlanRow[]
  const selections = useMemo(
    () => serializeFunnelSelections(funnelRows, choices),
    [funnelRows, choices],
  )

  const loadFunnels = useCallback(async () => {
    if (!code) return
    setFunnelState('loading')
    setFunnelError('')
    try {
      const data = await pipelineAPI.planRegenerationFunnels(
        code,
        categories,
        targets.map((target) => ({ marketplace: target.marketplace, seller_id: target.seller_id })),
      )
      setFunnelPlan(data)
      setChoices(initialFunnelChoices((data.rows || []) as FunnelPlanRow[]))
      setFunnelState('ready')
      setStep(3)
    } catch (error: any) {
      setFunnelPlan(null)
      setFunnelState('error')
      setFunnelError(errorMessage(error, 'Не удалось построить план контент-воронок'))
    }
  }, [code, categories, targets])

  const launch = async () => {
    if (!code) return
    setLaunching(true)
    setLaunchError('')
    try {
      const result = await pipelineAPI.regenerateSelectedVisuals(code, {
        categoryIds: categories,
        targets,
        funnelSelections: selections,
      })
      setJobId(result.job_id)
      setStep(4)
      onLaunched()
    } catch (error: any) {
      setLaunchError(errorMessage(error, 'Не удалось запустить перегенерацию'))
    } finally {
      setLaunching(false)
    }
  }

  const steps: Step[] = [
    { id: 'products', label: 'Товары', description: 'По матрице категорий' },
    { id: 'cabinets', label: 'Кабинеты', description: 'Куда пойдут визуалы' },
    { id: 'funnels', label: 'Контент-воронки', description: 'Чем рендерить' },
    ...(jobId ? [{ id: 'execution', label: 'Выполнение', description: 'Прогресс прогона' }] : []),
  ]

  const finished = isExecutionFinished(job)
  const failed = isExecutionFailed(job)
  const headline = executionHeadline(job)
  const stages = executionStages(job)

  const footer = jobId ? (
    <Button variant="primary" onClick={onClose}>
      {finished ? 'Закрыть' : 'Свернуть — работа продолжится'}
    </Button>
  ) : (
    <div className="flex w-full flex-wrap items-center gap-2">
      <Button variant="secondary" onClick={onClose} disabled={launching}>
        Отмена
      </Button>
      <Button
        variant="secondary"
        onClick={() => setStep((current) => (current > 1 ? ((current - 1) as WizardStep) : current))}
        disabled={step === 1 || launching}
      >
        Назад
      </Button>
      {step < 3 ? (
        <Button
          variant="primary"
          className="sm:ml-auto"
          loading={funnelState === 'loading'}
          disabled={
            planState !== 'ready'
            || (step === 1 && categories.length === 0)
            || (step === 2 && targets.length === 0)
          }
          onClick={() => {
            if (step === 1) setStep(2)
            else void loadFunnels()
          }}
        >
          Далее
        </Button>
      ) : (
        <Button
          variant="primary"
          className="sm:ml-auto"
          loading={launching}
          disabled={selections.length === 0 || launching}
          onClick={() => void launch()}
        >
          Перегенерировать
        </Button>
      )}
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={launching ? () => undefined : onClose}
      title="Перегенерировать визуалы"
      subtitle={code ? `Макет #${code}${plan?.design_type ? ` · ${plan.design_type}` : ''}` : undefined}
      size="xl"
      layer="nested"
      footer={footer}
      closeOnOverlayClick={false}
      closeOnEsc={!launching}
      showCloseButton={!launching}
    >
      <div className="flex gap-6">
        <div className="hidden w-44 shrink-0 pt-1 md:block">
          <Stepper
            steps={steps}
            currentIndex={step - 1}
            onStepClick={(index) => {
              if (jobId) return
              const target = (index + 1) as WizardStep
              if (target < step) setStep(target)
            }}
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-4 rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 dark:bg-zinc-800 dark:text-zinc-200 md:hidden">
            Шаг {step} из {steps.length} · {steps[step - 1]?.label}
          </div>

          {planState === 'loading' && (
            <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Загружаем товары и кабинеты макета…
            </p>
          )}
          {planState === 'error' && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
              <p>{planError}</p>
              <Button
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={() => void loadPlan(code)}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Повторить
              </Button>
            </div>
          )}

          {/* ── Шаг 1: товары по матрице категорий ── */}
          {step === 1 && planState === 'ready' && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
                  Товары макета
                </h4>
                <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                  Список — по матрице «тип макета → категории». Товары, которых ещё нет,
                  будут созданы при запуске. Визуалы категории рендерятся одной задачей,
                  поэтому отметка одного товара включает всю его категорию.
                </p>
                <p className="mt-2 text-xs font-medium text-gray-700 dark:text-zinc-300">
                  Выбрано товаров: {summary.products} · категорий: {summary.categories}
                  {summary.created > 0 ? ` · будет создано: ${summary.created}` : ''}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setCategories((plan?.categories || []).map((category) => category.category_id))
                  }
                >
                  Выбрать все
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setCategories([])}>
                  Снять выбор
                </Button>
              </div>

              <div className="space-y-1.5">
                {rows.map((row) => {
                  const checked = categories.includes(row.categoryId)
                  return (
                    <label
                      key={row.key}
                      className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm transition-colors ${
                        checked
                          ? 'border-indigo-400 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-950/30'
                          : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:bg-zinc-800'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        aria-label={`${row.categoryPrefix} ${row.article || 'новый товар'}`}
                        onChange={() =>
                          setCategories((current) => toggleCategory(current, row.categoryId))
                        }
                        className="h-4 w-4 accent-indigo-600"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block font-mono text-sm text-gray-900 dark:text-zinc-100">
                          {row.article || '— будет создан'}
                        </span>
                        <span className="block truncate text-xs text-gray-500 dark:text-zinc-400">
                          {row.categoryPrefix}
                          {row.categoryDescription ? ` · ${row.categoryDescription}` : ''}
                          {row.quantityTotal > 1
                            ? ` · товар ${row.quantityIndex} из ${row.quantityTotal}`
                            : ''}
                        </span>
                      </span>
                      {row.rendersWholeCategory && (
                        <span className="shrink-0 rounded border border-gray-200 px-2 py-1 text-2xs text-gray-500 dark:border-zinc-700 dark:text-zinc-400">
                          категория целиком
                        </span>
                      )}
                    </label>
                  )
                })}
              </div>

              {categories.length === 0 && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  Отметьте хотя бы один товар.
                </p>
              )}
            </div>
          )}

          {/* ── Шаг 2: кабинеты ── */}
          {step === 2 && planState === 'ready' && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
                  Кабинеты
                </h4>
                <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                  Отмечены кабинеты, которые уже участвовали в прогонах этого макета.
                  Новый кабинет получит визуалы и черновик карточки впервые.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {(plan?.cabinets || []).map((cabinet) => {
                  const key = cabinetKey(cabinet)
                  const checked = cabinets.includes(key)
                  return (
                    <label
                      key={key}
                      className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm transition-colors ${
                        checked
                          ? 'border-indigo-400 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-950/30'
                          : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:bg-zinc-800'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        aria-label={`${cabinet.marketplace.toUpperCase()}, ${cabinet.name}, кабинет ${cabinet.cabinet}`}
                        onChange={() =>
                          setCabinets((current) =>
                            current.includes(key)
                              ? current.filter((value) => value !== key)
                              : [...current, key],
                          )
                        }
                        className="h-4 w-4 accent-indigo-600"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-gray-900 dark:text-zinc-100">
                          {cabinet.name}
                        </span>
                        <span className="block truncate text-xs text-gray-500 dark:text-zinc-400">
                          {cabinet.marketplace.toUpperCase()} · кабинет {cabinet.cabinet}
                        </span>
                      </span>
                      {cabinet.used_before && (
                        <span className="shrink-0 rounded bg-gray-100 px-2 py-1 text-2xs text-gray-600 dark:bg-zinc-800 dark:text-zinc-300">
                          был в прогоне
                        </span>
                      )}
                    </label>
                  )
                })}
              </div>
              {(plan?.cabinets || []).length === 0 && (
                <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                  Активных кабинетов нет — перегенерировать визуалы некуда.
                </p>
              )}
              {targets.length === 0 && (plan?.cabinets || []).length > 0 && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  Отметьте хотя бы один кабинет.
                </p>
              )}
              {funnelState === 'error' && (
                <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                  <p>{funnelError}</p>
                  <Button variant="secondary" size="sm" className="mt-2" onClick={() => void loadFunnels()}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Повторить
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ── Шаг 3: контент-воронки ── */}
          {step === 3 && funnelPlan && (
            <div className="space-y-4">
              <ContentFunnelSelectionStep
                plan={{
                  canonical_targets: funnelPlan.canonical_targets as any,
                  rows: funnelRows,
                  funnels: funnelPlan.funnels,
                }}
                choices={choices}
                onChoiceChange={(rowKey, value) =>
                  setChoices((current) => ({ ...current, [rowKey]: value }))
                }
              />
              {selections.length === 0 && (
                <p className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                  <AlertTriangle className="h-4 w-4" />
                  Ни одна воронка не выбрана — генерировать нечего.
                </p>
              )}
              {launchError && (
                <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                  {launchError}
                </div>
              )}
            </div>
          )}

          {/* ── Шаг 4: выполнение ── */}
          {step === 4 && (
            <section className="space-y-5" aria-live="polite" aria-label="Выполнение перегенерации">
              <div
                className={`rounded-xl border p-4 ${
                  finished && !failed
                    ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/20'
                    : failed
                      ? 'border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/20'
                      : 'border-indigo-200 bg-indigo-50 dark:border-indigo-900 dark:bg-indigo-950/20'
                }`}
              >
                <div className="flex items-start gap-3">
                  {finished && !failed ? (
                    <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" />
                  ) : failed ? (
                    <XCircle className="mt-0.5 h-6 w-6 shrink-0 text-red-600" />
                  ) : (
                    <Loader2 className="mt-0.5 h-6 w-6 shrink-0 animate-spin text-indigo-600" />
                  )}
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">
                      {headline.title}
                    </h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-zinc-300">
                      {headline.description}
                    </p>
                    <p className="mt-1 break-all text-xs text-gray-500">Задача: {jobId}</p>
                  </div>
                </div>
              </div>

              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {stages.map((card) => (
                  <div
                    key={card.key || card.label}
                    className={`rounded-lg border bg-white p-3 dark:bg-zinc-900 ${
                      card.state === 'failed'
                        ? 'border-red-300 dark:border-red-900'
                        : card.state === 'running'
                          ? 'border-indigo-300 dark:border-indigo-800'
                          : 'border-gray-200 dark:border-zinc-700'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-gray-600 dark:text-zinc-300">
                        {card.label}
                      </span>
                      {card.state === 'done' ? (
                        <Check className="h-4 w-4 text-emerald-600" />
                      ) : card.state === 'running' ? (
                        <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
                      ) : card.state === 'failed' ? (
                        <XCircle className="h-4 w-4 text-red-600" />
                      ) : (
                        <Minus className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                    <p className="mt-2 text-lg font-semibold text-gray-900 dark:text-zinc-100">
                      {card.determinate === false ? (
                        <span className="text-sm font-normal text-gray-500">
                          Определяем количество…
                        </span>
                      ) : (
                        <>
                          {card.current}{' '}
                          <span className="text-sm font-normal text-gray-400">из {card.total}</span>
                        </>
                      )}
                    </p>
                    {card.failed > 0 && <p className="mt-1 text-xs text-red-600">Ошибок: {card.failed}</p>}
                    {card.skipped > 0 && (
                      <p className="mt-1 text-xs text-amber-600">Пропущено: {card.skipped}</p>
                    )}
                  </div>
                ))}
              </div>

              {stages.length === 0 && !finished && (
                <div className="flex min-h-24 items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 text-sm text-gray-500 dark:border-zinc-700">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Подготавливаем прогон…
                </div>
              )}
              {pollError && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                  Не удалось обновить экран: {pollError}. Повторяем автоматически.
                </div>
              )}
              {!finished && (
                <p className="text-xs text-gray-500">
                  Окно можно закрыть: прогон продолжится в фоне, а задача останется в индикаторе
                  фоновых работ.
                </p>
              )}
            </section>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default RegenerateVisualsWizard
